// Pure search + note helpers for the daily field report's "task -> cost code"
// picker. No React, no JSX, no imports — driven directly by
// scripts/test-scope-search.mjs.
//
// Scope items come from job_scope_items (PlanSwift estimate tasks, NO money):
//   { cost_code, task_name, size_text, qty, unit, man_days, area, crew, search_terms }
// search_terms is the crew task phrases joined with " | " ("Form and Strip - Wall").
// Picking a task sets the row's cost code (the ONLY value payroll uses) and writes
// the task name into the row's notes via noteAfterPick().

// Lower-case, accent-folded, whitespace-collapsed.
export function foldText(s) {
  return String(s == null ? '' : s)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/\s+/g, ' ').trim()
}

// Numeric cost-code order (100 < 300 < 9003); non-numeric codes sort last.
export function codeSortKey(code) {
  const n = Number(String(code == null ? '' : code).trim())
  return Number.isFinite(n) && String(code).trim() !== '' ? n : Number.MAX_SAFE_INTEGER
}
function byCode(a, b) {
  return (codeSortKey(a) - codeSortKey(b)) || String(a).localeCompare(String(b))
}

function crewPhrases(item) {
  const out = []
  const add = p => { const t = String(p || '').trim(); if (t && !out.includes(t)) out.push(t) }
  String(item && item.search_terms ? item.search_terms : '').split('|').forEach(add)
  ;(Array.isArray(item && item.crew) ? item.crew : []).forEach(c => add(c && c.task))
  return out
}

// Search the job's scope. EVERY query token must appear somewhere in: the code,
// the code's description, the task name, the size text, or the crew phrases.
// Returns groups in numeric cost-code order:
//   [{ code, description, items: [{ item, matchedPhrase }] }]
// matchedPhrase names the crew phrase that explains the hit ("Form and Strip -
// Wall") when a token was found there but NOT in the task name / size text — the
// hint for "why did this show up". Empty query -> every task, no hints.
export function searchScope(items, costCodes, query) {
  const list = Array.isArray(items) ? items : []
  const desc = new Map((Array.isArray(costCodes) ? costCodes : []).map(c => [String(c.code).trim(), c.description || '']))
  const tokens = foldText(query).split(' ').filter(Boolean)
  const groups = new Map()
  for (const item of list) {
    if (!item || !item.task_name) continue
    const code = String(item.cost_code == null ? '' : item.cost_code).trim()
    const phrases = crewPhrases(item)
    const own = foldText(`${item.task_name} ${item.size_text || ''}`)
    const hay = foldText([code, desc.get(code) || '', item.task_name, item.size_text || '', phrases.join(' | ')].join(' | '))
    if (!tokens.every(t => hay.includes(t))) continue
    let matchedPhrase = null
    if (tokens.length) {
      const extra = tokens.filter(t => !own.includes(t))
      if (extra.length) matchedPhrase = phrases.find(p => extra.some(t => foldText(p).includes(t))) || null
    }
    if (!groups.has(code)) groups.set(code, { code, description: desc.get(code) || '', items: [] })
    groups.get(code).items.push({ item, matchedPhrase })
  }
  return [...groups.values()].sort((a, b) => byCode(a.code, b.code))
}

// The row's note after the foreman picks a task:
//   blank note                              -> the task name
//   note equal to ANY task name on this job -> replaced (it was an earlier pick)
//   anything the foreman typed              -> kept, " / <task name>" appended
//                                              unless already contained
export function noteAfterPick(currentNote, pickedTask, allTaskNames) {
  const name = String((pickedTask && typeof pickedTask === 'object' ? pickedTask.task_name : pickedTask) || '').trim()
  const cur = String(currentNote == null ? '' : currentNote).trim()
  if (!name) return cur || null
  if (!cur) return name
  const known = new Set((Array.isArray(allTaskNames) ? allTaskNames : []).map(n => foldText(n)).filter(Boolean))
  if (known.has(foldText(cur))) return name
  if (foldText(cur).includes(foldText(name))) return cur
  return `${cur} / ${name}`
}

// "4.0 MD / 32 hrs" (hours = man-days x 8). '' when there are no man-days.
export function fmtManDays(md) {
  const n = Number(md)
  if (!(n > 0)) return ''
  return `${n.toFixed(1)} MD / ${Math.round(n * 8)} hrs`
}
